import { CONTACT_LIMITS, NO_OWNER, type ContactLink, type ContactListQuery } from '../contract';

/** The "Eu" value of the owner filter (the API takes the member's id). */
export const OWNER_ME = 'me';

/**
 * The state of the contacts list in the URL, in English:
 * `?q=&link=with|without&owner=me|none|<id>&archived=true&page=`.
 * `owner` is "Eu", "Sem responsável" or a member.
 */
export interface ContactListSearch {
  q?: string;
  link?: ContactLink;
  owner?: string;
  archived?: true;
  page?: number;
}

function text(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() === '' ? undefined : value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

/** Reads the state from the URL; anything malformed is dropped (never an error page). */
export function parseContactListSearch(raw: Record<string, unknown>): ContactListSearch {
  const search: ContactListSearch = {};
  const q = text(raw['q'])?.trim().slice(0, CONTACT_LIMITS.searchMax);
  if (q) search.q = q;
  if (raw['link'] === 'with' || raw['link'] === 'without') search.link = raw['link'];
  const owner = text(raw['owner']);
  if (owner) search.owner = owner;
  if (raw['archived'] === true || raw['archived'] === 'true') search.archived = true;
  const page = Number(raw['page']);
  if (Number.isInteger(page) && page > 1) search.page = page;
  return search;
}

/** The API query for the state; "Eu" is the caller's membership id. */
export function toContactListQuery(
  search: ContactListSearch,
  membershipId: string | undefined,
): ContactListQuery {
  const owner = search.owner === OWNER_ME ? membershipId : search.owner;
  return {
    ...(search.q ? { q: search.q } : {}),
    ...(search.link ? { link: search.link } : {}),
    ...(owner ? { ownerMembershipId: owner } : {}),
    ...(search.archived ? { archived: true } : {}),
    ...(search.page ? { page: search.page } : {}),
  };
}

export const hasActiveFilters = (search: ContactListSearch): boolean =>
  Boolean(search.q ?? search.link ?? search.owner ?? search.archived);

/** The state with these changes, back at the first page. */
export function withFilters(
  search: ContactListSearch,
  changes: Partial<ContactListSearch>,
): ContactListSearch {
  const next = { ...search, ...changes };
  delete next.page;
  return next;
}

export const clearedSearch = (): ContactListSearch => ({});

export { NO_OWNER };
